import React, { useState } from 'react';
import { EnvelopeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { validateEmail, sanitizeEmail } from '../utils/emailValidation';

// API base URL - use relative URL for production to work with Vercel
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5001/api'
  : '/api';

interface NewsletterSignupProps {
  variant?: 'default' | 'hero';
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Email validation
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setStatus('error');
      setMessage(validation.message);
      return;
    }

    const sanitizedEmail = sanitizeEmail(email);
    setStatus('loading');
    
    try {
      console.log('Submitting to:', `${API_BASE_URL}/newsletter/subscribe`);
      
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: sanitizedEmail }),
      });

      console.log('Response status:', response.status);
      
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Welcome! You\'ve been successfully subscribed to Destress Daily.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  if (variant === 'hero') {
    return (
      <section className="newsletter-signup text-white py-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center fade-in">
            <div className="flex justify-center mb-4">
              <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-full p-3 floating">
                <EnvelopeIcon className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Get Destress Daily
            </h2>
            <p className="text-lg text-white text-opacity-90 mb-4 max-w-2xl mx-auto">
              Join thousands of health enthusiasts and receive practical wellness tips delivered to your inbox every day.
            </p>
            
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="form-input newsletter-input flex-1"
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="btn-primary bg-white text-primary-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? (
                    <div className="loading-spinner h-5 w-5"></div>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
              
              {status === 'success' && (
                <div className="mt-4 p-4 bg-green-500 bg-opacity-20 backdrop-blur-sm rounded-xl border border-green-300 border-opacity-30">
                  <div className="flex items-center justify-center text-green-100">
                    <CheckIcon className="h-5 w-5 mr-2" />
                    {message}
                  </div>
                </div>
              )}
              
              {status === 'error' && (
                <div className="mt-4 p-4 bg-red-500 bg-opacity-20 backdrop-blur-sm rounded-xl border border-red-300 border-opacity-30">
                  <div className="text-red-100 text-sm">
                    {message}
                  </div>
                </div>
              )}
            </form>
            
            <p className="text-sm text-white text-opacity-70 mt-6">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 bg-gradient-to-br from-primary-50 to-health-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-primary-100/20 to-health-100/20"></div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="glass rounded-3xl p-4 md:p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-primary-gradient rounded-full p-3 floating">
              <EnvelopeIcon className="h-6 w-6 text-white" />
            </div>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Stay Updated with Destress Daily
          </h2>
          <p className="text-lg text-gray-600 mb-4 max-w-2xl mx-auto">
            Get the latest health tips, wellness advice, and practical hacks delivered straight to your inbox. Join our community of health enthusiasts!
          </p>
          
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="form-input newsletter-input flex-1"
                disabled={status === 'loading'}
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? (
                  <div className="loading-spinner h-5 w-5"></div>
                ) : (
                  'Subscribe'
                )}
              </button>
            </div>
            
            {status === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center justify-center text-green-700">
                  <CheckIcon className="h-5 w-5 mr-2" />
                  {message}
                </div>
              </div>
            )}
            
            {status === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="text-red-700 text-sm">
                  {message}
                </div>
              </div>
            )}
          </form>
          
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
            <div className="flex items-center justify-center">
              <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
              Daily health tips
            </div>
            <div className="flex items-center justify-center">
              <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
              Expert wellness advice
            </div>
            <div className="flex items-center justify-center">
              <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
              Unsubscribe anytime
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup; 